// The editor's Preview plays a media area set to a saved playlist, as the screen's
// answer would fill it (internal/api signageplay.go). Campaigns are not previewed.
import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import type { LayoutDocument, LayoutText, MediaItem } from './types'

export interface PlayedList {
  items: MediaItem[]
  ticker: LayoutText[]
}

/** The saved playlists a design's media areas name, each once. */
export function playlistIdsOf(doc: LayoutDocument): string[] {
  const ids = doc.elements
    .filter((el) => el.kind === 'media' && el.media?.source === 'playlist' && el.media.playlistId)
    .map((el) => el.media!.playlistId!)
  return [...new Set(ids)]
}

/** The design with each saved playlist's items and ticker in its media areas. */
export function withPlayedLists(doc: LayoutDocument, lists: Readonly<Record<string, PlayedList>>): LayoutDocument {
  const listOf = (el: LayoutDocument['elements'][number]) =>
    el.kind === 'media' && el.media?.source === 'playlist' && el.media.playlistId ? lists[el.media.playlistId] : undefined
  if (!doc.elements.some(listOf)) return doc
  return {
    ...doc,
    elements: doc.elements.map((el) => {
      const list = listOf(el)
      return list ? { ...el, media: { ...el.media!, items: list.items, ticker: list.ticker } } : el
    }),
  }
}

/** The design as Preview plays it: saved playlists fetched once each while it is open. */
export function usePlayedDocument(doc: LayoutDocument): LayoutDocument {
  const [lists, setLists] = useState<Record<string, PlayedList>>({})
  const wanted = playlistIdsOf(doc).join(',')
  useEffect(() => {
    let live = true
    for (const id of wanted ? wanted.split(',') : []) {
      api
        .designPlaylistPlayed(id)
        .then((list) => live && setLists((was) => ({ ...was, [id]: list })))
        .catch(() => {})
    }
    return () => {
      live = false
    }
  }, [wanted])
  return useMemo(() => withPlayedLists(doc, lists), [doc, lists])
}
